import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart3, SlidersHorizontal } from 'lucide-react';
import { API_BASE_URL } from '../../config';
import SymbolCard from './SymbolCard';
import Pagination from '../Pagination';
import ListLimitBanner from '../ui/ListLimitBanner';

function getAuthHeader() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const WINDOW_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: '4h',  label: '4 Hours' },
  { value: '24h', label: '24 Hours' },
  { value: '1d',  label: '1 Day' },
  { value: '3d',  label: '3 Days' },
  { value: '7d',  label: '7 Days' },
  { value: '15d', label: '15 Days' },
  { value: '1m',  label: '1 Month' },
  { value: '3m',  label: '3 Months' },
  { value: '1y',  label: '1 Year' },
];

const SORT_OPTIONS = [
  { value: '-analyst_count',    label: 'Most Analysts' },
  { value: '-prediction_count', label: 'Most Predictions' },
  { value: '-bullish_count',    label: 'Most Bullish' },
  { value: '-bearish_count',    label: 'Most Bearish' },
  { value: 'symbol',            label: 'A → Z' },
];

const PAGE_SIZE = 40;

export default function SymbolGallery({ noLink = false, onCardClick }) {
  const [symbols, setSymbols] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [ordering, setOrdering] = useState('-analyst_count');
  const [since, setSince] = useState('all');
  const [exchange, setExchange] = useState('');
  const [sector, setSector] = useState('');
  const [industry, setIndustry] = useState('');
  const [filterOptions, setFilterOptions] = useState({ exchanges: [], sectors: [], industries: [] });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [symbolsListLimit, setSymbolsListLimit] = useState(null);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const activeFilterCount = [exchange, sector, industry, since !== 'all' ? since : ''].filter(Boolean).length;

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Fetch filter options; re-fetch industries when sector changes
  useEffect(() => {
    const params = sector ? `?sector=${encodeURIComponent(sector)}` : '';
    axios.get(`${API_BASE_URL}/api/research/symbols/filter-options/${params}`, { headers: getAuthHeader() })
      .then((res) => setFilterOptions(res.data))
      .catch(() => {});
  }, [sector]);

  // Reset industry when sector changes
  useEffect(() => {
    setIndustry('');
  }, [sector]);

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, ordering, since, exchange, sector, industry]);

  useEffect(() => {
    const fetchSymbols = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ ordering });
        if (debouncedSearch) params.set('search', debouncedSearch);
        if (since !== 'all') params.set('since', since);
        if (exchange) params.set('exchange', exchange);
        if (sector) params.set('sector', sector);
        if (industry) params.set('industry', industry);
        params.set('page', page);
        params.set('page_size', PAGE_SIZE);
        const res = await axios.get(`${API_BASE_URL}/api/research/symbols/?${params}`, {
          headers: getAuthHeader(),
        });
        const data = res.data;
        if (data && typeof data === 'object' && 'results' in data) {
          setSymbols(data.results);
          setTotalCount(data.count ?? 0);
          if (data.list_limit_reached) {
            setSymbolsListLimit({ limit: data.list_limit, tier: data.list_limit_tier });
          } else {
            setSymbolsListLimit(null);
          }
        } else {
          setSymbols(Array.isArray(data) ? data : []);
          setTotalCount(Array.isArray(data) ? data.length : 0);
          setSymbolsListLimit(null);
        }
      } catch (err) {
        console.error('Failed to load symbols', err);
        setSymbols([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };
    fetchSymbols();
  }, [debouncedSearch, ordering, since, exchange, sector, industry, page]);

  const windowLabel = WINDOW_OPTIONS.find((o) => o.value === since)?.label ?? 'All time';

  return (
    <div>
{/* Mobile: search + filter toggle */}
      <div className="flex md:hidden gap-2 mb-3">
        <input
          type="text"
          placeholder="Search symbol or company..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
        />
        <button
          onClick={() => setFiltersOpen((v) => !v)}
          className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors flex-shrink-0 ${
            activeFilterCount > 0
              ? 'bg-gray-900 text-white border-gray-900'
              : 'bg-white text-gray-600 border-gray-200'
          }`}
        >
          <SlidersHorizontal size={14} />
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-1 bg-white text-gray-900 text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Mobile expanded filter panel */}
      {filtersOpen && (
        <div className="md:hidden grid grid-cols-2 gap-2 mb-4 p-4 bg-white border border-gray-200 rounded-xl">
          <select
            value={exchange}
            onChange={(e) => setExchange(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          >
            <option value="">All Exchanges</option>
            {filterOptions.exchanges.map((ex) => (
              <option key={ex} value={ex}>{ex}</option>
            ))}
          </select>
          <select
            value={sector}
            onChange={(e) => setSector(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          >
            <option value="">All Sectors</option>
            {filterOptions.sectors.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          >
            <option value="">All Industries</option>
            {filterOptions.industries?.map((ind) => (
              <option key={ind} value={ind}>{ind}</option>
            ))}
          </select>
          <select
            value={since}
            onChange={(e) => setSince(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          >
            {WINDOW_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            value={ordering}
            onChange={(e) => setOrdering(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      )}

      {/* Desktop: full controls */}
      <div className="hidden md:flex flex-wrap gap-3 mb-5">
        <input
          type="text"
          placeholder="Search symbol or company..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[180px] px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
        />
        <select
          value={exchange}
          onChange={(e) => setExchange(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
        >
          <option value="">All Exchanges</option>
          {filterOptions.exchanges.map((ex) => (
            <option key={ex} value={ex}>{ex}</option>
          ))}
        </select>
        <select
          value={sector}
          onChange={(e) => setSector(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
        >
          <option value="">All Sectors</option>
          {filterOptions.sectors.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select
          value={industry}
          onChange={(e) => setIndustry(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
        >
          <option value="">All Industries</option>
          {filterOptions.industries?.map((ind) => (
            <option key={ind} value={ind}>{ind}</option>
          ))}
        </select>
        <select
          value={since}
          onChange={(e) => setSince(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
        >
          {WINDOW_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          value={ordering}
          onChange={(e) => setOrdering(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 animate-pulse h-32" />
          ))}
        </div>
      ) : symbols.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <BarChart3 size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium">No symbols found.</p>
          <p className="text-sm mt-1">
            {debouncedSearch
              ? 'Try a different search term.'
              : since !== 'all'
              ? `No analyst predictions in the last ${windowLabel.toLowerCase()}.`
              : 'No analyst predictions have been extracted yet.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {symbols.map((sym) => (
            <SymbolCard key={`${sym.exchange}-${sym.ticker}`} symbol={sym} noLink={noLink} onCardClick={onCardClick} />
          ))}
        </div>
      )}

      <Pagination page={page} totalPages={totalPages} onPage={setPage} />
      {symbolsListLimit && (
        <ListLimitBanner limit={symbolsListLimit.limit} type="symbol" />
      )}
    </div>
  );
}
